'use client';

import { IconButton, Menu, MenuItem, Typography } from '@mui/material';
import { useState } from 'react';

import { type Currency, useCurrency } from '@/configurations/providers/CurrencyProvider';
import { type NavTriggerContext, trackNavigationClick } from '@/configurations/tracking/navigation';

type CurrencySwitcherProps = {
    /**
     * Adapte la couleur selon le contexte navbar : `overlay` (hero
     * glassmorphique) → blanc, `solid` (navbar blanche) → secondary.
     */
    tone?: 'overlay' | 'solid';
    triggerContext: NavTriggerContext;
};

const CURRENCY_SYMBOL: Record<Currency, string> = { EUR: '€', USD: '$' };
const CURRENCIES: Currency[] = ['USD', 'EUR'];

export function CurrencySwitcher({ tone = 'solid', triggerContext }: CurrencySwitcherProps) {
    const { currency, setCurrency } = useCurrency();
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const open = Boolean(anchorEl);

    const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
        trackNavigationClick({ navItem: 'currency_switcher', triggerContext });
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleSelect = (next: Currency) => {
        handleClose();
        setCurrency(next);
    };

    const color = tone === 'overlay' ? 'common.white' : 'text.secondary';

    return (
        <>
            <IconButton
                onClick={handleOpen}
                aria-label="Change currency"
                aria-haspopup="menu"
                aria-expanded={open}
                data-testid="currency-switcher"
                sx={{ p: 0.75, color, transition: 'color 200ms ease' }}
            >
                <Typography component="span" sx={{ fontWeight: 600, fontSize: 18, lineHeight: 1 }}>
                    {CURRENCY_SYMBOL[currency]}
                </Typography>
            </IconButton>
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                {CURRENCIES.map((code) => (
                    <MenuItem
                        key={code}
                        selected={code === currency}
                        onClick={() => handleSelect(code)}
                        data-testid={`currency-option-${code}`}
                    >
                        <Typography>{`${CURRENCY_SYMBOL[code]} ${code}`}</Typography>
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
}
